import { useEffect, useState } from "react";
import { Download, X, CheckCircle2, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, getDeviceId } from "@/lib/api";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWABadge() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [code, setCode] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("trio_pwa_dismissed") === "1"
  );
  const [codeHidden, setCodeHidden] = useState(
    () => localStorage.getItem("trio_discount_code_hidden") === "1"
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    if (isStandalone()) setInstalled(true);

    const stored = localStorage.getItem("trio_discount_code");
    if (stored) setCode(stored);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function claimCode(e) {
    e?.preventDefault();
    try {
      const { data } = await api.post("/app-installs", {
        device_id: getDeviceId(),
        name: name || null,
        email: email || null,
      });
      localStorage.setItem("trio_discount_code", data.discount_code);
      setCode(data.discount_code);
      toast.success("You're in! Show this code in-store for your discount.");
    } catch {
      toast.error("Couldn't save your code. Try again.");
    }
  }

  async function handleInstall() {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
        await claimCode();
      }
      setDeferred(null);
    } else {
      // iOS / unsupported — show instructions in dialog
      setOpen(true);
    }
  }

  function copyCode() {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    toast.success("Code copied");
  }

  // Floating badge (collapsed)
  if (dismissed && !code) return null;
  if (code && codeHidden) return null;

  return (
    <>
      {/* Floating CTA bottom-left so it doesn't conflict with emergent badge */}
      <div
        data-testid="pwa-floating"
        className="fixed bottom-4 left-4 z-30 max-w-[300px]"
      >
        {code ? (
          <div
            data-testid="pwa-discount-card"
            className="bg-[var(--trio-text)] text-[var(--trio-bg)] p-4 shadow-2xl border border-[var(--trio-accent)] flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-[var(--trio-accent)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--trio-accent)] font-bold">
                Your in-store discount
              </div>
              <div className="font-display uppercase text-lg truncate tracking-tight" data-testid="pwa-code">
                {code}
              </div>
            </div>
            <button
              onClick={copyCode}
              data-testid="pwa-copy"
              className="p-2 hover:text-[var(--trio-accent)]"
              aria-label="Copy code"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                localStorage.setItem("trio_discount_code_hidden", "1");
                setCodeHidden(true);
              }}
              data-testid="pwa-code-close"
              className="p-2 opacity-70 hover:opacity-100"
              aria-label="Close discount code"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="bg-[var(--trio-primary)] text-white p-4 pr-3 shadow-2xl flex items-start gap-3 border border-[var(--trio-primary-hover)]">
            <Download className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[9px] tracking-[0.25em] uppercase font-bold opacity-80">
                Save 10%
              </div>
              <div className="text-sm leading-snug mb-2">
                Add Trio to your home screen — get a one-time in-store discount code.
              </div>
              <button
                onClick={handleInstall}
                data-testid="pwa-install-btn"
                className="bg-white text-[var(--trio-primary)] px-3 py-1.5 text-[11px] tracking-widest uppercase font-bold hover:bg-[var(--trio-bg)] transition-colors"
              >
                Add to Home Screen
              </button>
            </div>
            <button
              onClick={() => {
                localStorage.setItem("trio_pwa_dismissed", "1");
                setDismissed(true);
              }}
              data-testid="pwa-dismiss"
              className="opacity-70 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="pwa-install-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-3xl tracking-tight">
              Add Trio to your Home Screen
            </DialogTitle>
            <DialogDescription className="text-[var(--trio-muted)]">
              Get one-tap access — and a one-time 10% in-store discount code.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <p className="font-medium">On iPhone (Safari):</p>
            <ol className="list-decimal list-inside space-y-1 text-[var(--trio-muted)]">
              <li>Tap the Share button.</li>
              <li>Scroll and tap <em>“Add to Home Screen”</em>.</li>
              <li>Tap <em>Add</em>. Open Trio from your Home Screen.</li>
            </ol>
            <p className="font-medium mt-4">On Android (Chrome):</p>
            <ol className="list-decimal list-inside space-y-1 text-[var(--trio-muted)]">
              <li>Tap the menu (⋮).</li>
              <li>Tap <em>“Install app”</em> or <em>“Add to Home Screen”</em>.</li>
            </ol>
          </div>

          <form onSubmit={claimCode} className="mt-2 space-y-3 border-t border-[var(--trio-border)] pt-5">
            <p className="text-sm font-medium">Claim your discount code:</p>
            <div>
              <Label htmlFor="pwa-name" className="text-xs uppercase tracking-widest">
                Name (optional)
              </Label>
              <Input
                id="pwa-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="pwa-name"
                className="mt-1 rounded-none border-[var(--trio-border)]"
              />
            </div>
            <div>
              <Label htmlFor="pwa-email" className="text-xs uppercase tracking-widest">
                Email (optional)
              </Label>
              <Input
                id="pwa-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="pwa-email"
                className="mt-1 rounded-none border-[var(--trio-border)]"
              />
            </div>
            <button
              type="submit"
              data-testid="pwa-claim-btn"
              className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase transition-colors"
            >
              Get My Discount Code
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
